import type React from 'react';
import { motion } from 'framer-motion';
import { fadeIn, fadeInUp, fadeInLeft, fadeInRight } from '../utils/animations';
import Button from './ui/Button';
import ContactForm from './ui/ContactForm';
import { COMPANY_INFO } from '../utils/constants';
import heroImage from '../assets/images/hero-bg.jpg';
import logoSvg from '../assets/images/logo.svg';

const HeroSection: React.FC = () => {
  return (
    <section
      id="hero"
      className="relative min-h-screen pt-20"
      style={{
        backgroundImage: `linear-gradient(rgba(26, 34, 38, 0.85), rgba(26, 34, 38, 0.9)), url(${heroImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="container mx-auto flex min-h-[calc(100vh-80px)] flex-col px-6 pb-16 pt-12 lg:px-8">
        <div className="grid flex-1 items-center gap-8 lg:grid-cols-2">
          {/* Left column - Text content */}
          <motion.div
            className="flex flex-col justify-center text-white"
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <motion.div
              className="mb-6 flex items-center"
              variants={fadeInLeft}
            >
              <span className="inline-block rounded bg-secondary/20 px-4 py-1 text-sm font-semibold text-secondary">
                С 2005 года на рынке IT-услуг
              </span>
            </motion.div>

            <motion.h1
              className="mb-6 text-4xl font-bold leading-tight md:text-5xl lg:text-6xl"
              variants={fadeInUp}
            >
              Решаем IT-задачи вашего бизнеса
            </motion.h1>

            <motion.p
              className="mb-8 text-lg text-gray-200 md:text-xl"
              variants={fadeInUp}
            >
              Полный цикл IT-услуг: проектирование, внедрение, сопровождение.
              Работаем честно, гибко и с гарантией результата для малого и среднего бизнеса.
            </motion.p>

            <motion.div
              className="mb-12 flex flex-wrap gap-4"
              variants={fadeInUp}
            >
              <Button
                variant="secondary"
                size="lg"
                withArrow
                onClick={() => {
                  const servicesSection = document.getElementById('services');
                  servicesSection?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Узнать о услугах
              </Button>

              <Button
                variant="outline"
                size="lg"
                onClick={() => {
                  const contactForm = document.getElementById('main-cta');
                  contactForm?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Получить консультацию
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div
              className="grid grid-cols-2 gap-8 md:grid-cols-3"
              variants={fadeInUp}
            >
              <div className="text-center">
                <span className="block text-3xl font-bold text-secondary">20+</span>
                <span className="text-sm text-gray-300">Лет опыта</span>
              </div>

              <div className="text-center">
                <span className="block text-3xl font-bold text-secondary">100+</span>
                <span className="text-sm text-gray-300">Реализованных проектов</span>
              </div>

              <div className="text-center">
                <span className="block text-3xl font-bold text-secondary">24/7</span>
                <span className="text-sm text-gray-300">Техническая поддержка</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right column - Form */}
          <motion.div
            className="hidden lg:block"
            initial="hidden"
            animate="visible"
            variants={fadeInRight}
          >
            <div className="overflow-hidden rounded-lg bg-primary/70 p-8 backdrop-blur-sm border border-secondary/20">
              <h3 className="mb-6 text-2xl font-bold text-white">
                Оставьте заявку на бесплатную консультацию
              </h3>
              <ContactForm light withComment={false} />
            </div>
          </motion.div>
        </div>

        {/* Scroll down indicator */}
        <motion.div
          className="mt-8 flex justify-center"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            repeat: Number.POSITIVE_INFINITY,
            repeatType: 'reverse',
            duration: 1.5,
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="text-secondary"
          >
            <path
              d="M12 5V19M12 19L19 12M12 19L5 12"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
